import React from 'react';
import { IonSelect, IonSelectOption } from '@ionic/react';
import { chevronDownOutline } from 'ionicons/icons';
import type {
  AssistantMode,
  ManufacturerOption,
  ServiceTitanConnectionStatus,
} from '../../services/chatApi';
import './AssistantScopeControls.css';

interface AssistantScopeControlsProps {
  manufacturers: ManufacturerOption[];
  manufacturerSlug: string;
  mode: AssistantMode;
  loading?: boolean;
  error?: string | null;
  serviceTitan: ServiceTitanConnectionStatus;
  connectingServiceTitan?: boolean;
  disabled?: boolean;
  onManufacturerChange: (slug: string) => void;
  onModeChange: (mode: AssistantMode) => void;
  onServiceTitanConnect: () => void;
}

const AssistantScopeControls: React.FC<AssistantScopeControlsProps> = ({
  manufacturers,
  manufacturerSlug,
  mode,
  loading = false,
  error,
  serviceTitan,
  connectingServiceTitan = false,
  disabled = false,
  onManufacturerChange,
  onModeChange,
  onServiceTitanConnect,
}) => {
  const selectedManufacturer = manufacturers.find((item) => item.slug === manufacturerSlug);
  const partsSupported = Boolean(selectedManufacturer?.partsSupported);

  return (
    <div className="assistant-scope" aria-label="SmartDocs answer scope">
      <div className="assistant-scope__selectors">
        {mode !== 'servicetitan' && <>
          <IonSelect
            className="assistant-scope__selector assistant-scope__manufacturer"
            aria-label="Manufacturer"
            interface="popover"
            interfaceOptions={{ header: 'Manufacturer' }}
            placeholder={loading ? 'Loading manufacturers…' : 'Select Manufacturer'}
            toggleIcon={chevronDownOutline}
            value={manufacturerSlug}
            disabled={disabled || loading || manufacturers.length === 0}
            onIonChange={(event) => onManufacturerChange(event.detail.value)}
          >
            {manufacturers.map((manufacturer) => (
              <IonSelectOption key={manufacturer.slug} value={manufacturer.slug}>
                {manufacturer.displayName}
              </IonSelectOption>
            ))}
          </IonSelect>
          <span className="assistant-scope__separator" aria-hidden="true">·</span>
        </>}
        <IonSelect
          className="assistant-scope__selector assistant-scope__modes"
          aria-label="Answer type"
          interface="popover"
          interfaceOptions={{ header: 'Mode' }}
          toggleIcon={chevronDownOutline}
          value={mode}
          disabled={disabled}
          onIonChange={(event) => {
            const nextMode = event.detail.value as AssistantMode;
            onModeChange(nextMode);
            if (nextMode === 'servicetitan' && !serviceTitan.connected) {
              onServiceTitanConnect();
            }
          }}
        >
          <IonSelectOption value="manuals">
            Manuals &amp; Specs
          </IonSelectOption>
          <IonSelectOption
            value="parts"
            disabled={disabled || !manufacturerSlug || !partsSupported}
          >
            Parts
          </IonSelectOption>
          <IonSelectOption
            value="ahri"
            disabled={disabled || !manufacturerSlug}
          >
            AHRI Directory
          </IonSelectOption>
          {serviceTitan.available && (
            <IonSelectOption value="servicetitan" disabled={connectingServiceTitan}>
              {connectingServiceTitan ? 'Connecting…' : 'ServiceTitan'}
            </IonSelectOption>
          )}
        </IonSelect>
      </div>
      {mode !== 'servicetitan' && !manufacturerSlug && !loading && (
        <span className="assistant-scope__hint">Choose a manufacturer before asking SmartDocs.</span>
      )}
      {mode !== 'servicetitan' && manufacturerSlug && !partsSupported && (
        <span className="assistant-scope__hint">Live Parts is not available for this manufacturer.</span>
      )}
      {mode === 'servicetitan' && serviceTitan.connected && (
        <span className="assistant-scope__connection is-connected">
          {serviceTitan.equipment_write_enabled
            ? 'ServiceTitan · Equipment access'
            : 'ServiceTitan · Connected'}
        </span>
      )}
      {mode === 'servicetitan' && serviceTitan.connected && serviceTitan.requires_reauthorization && (
        <span className="assistant-scope__hint">
          {serviceTitan.message}{' '}
          <button
            className="assistant-scope__connect"
            type="button"
            disabled={disabled || connectingServiceTitan}
            onClick={onServiceTitanConnect}
          >
            Reconnect ServiceTitan
          </button>
        </span>
      )}
      {mode === 'servicetitan' && !serviceTitan.connected && (
        <span className="assistant-scope__hint">
          {serviceTitan.message}{' '}
          <button
            className="assistant-scope__connect"
            type="button"
            disabled={disabled || connectingServiceTitan}
            onClick={onServiceTitanConnect}
          >
            {serviceTitan.requires_reauthorization ? 'Reconnect ServiceTitan' : 'Connect ServiceTitan'}
          </button>
        </span>
      )}
      {error && <span className="assistant-scope__error" role="alert">{error}</span>}
    </div>
  );
};

export default AssistantScopeControls;
